// R74 — Compact, gedeelde badge-row voor multi-type én multi-leverancier weergave.
// R77 — Optioneel per-item meta (toneOverride + icoon + tooltip) voor o.a. de
// "Sikkens Center" leverancier-badge die de export-status visualiseert.
import * as React from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { OrderDecoprofType } from "@/lib/ordersDecoprof";

type Variant = "type" | "supplier";

const TYPE_TONE: Record<OrderDecoprofType, string> = {
  Verfbestelling:     "bg-orange-100 text-orange-800 ring-orange-200",
  Behangorder:        "bg-teal-100 text-teal-800 ring-teal-200",
  Spuitbusbestelling: "bg-amber-100 text-amber-800 ring-amber-200",
  Overige:            "bg-pink-100 text-pink-800 ring-pink-200",
};

const SUPPLIER_TONE = "bg-slate-100 text-slate-700 ring-slate-200";
const NEUTRAL_TONE  = "bg-slate-100 text-slate-600 ring-slate-200";

// Geünificeerd pill-recipe: zelfde hoogte/font/padding/border-radius als status-badge
// en als de "Klant inlichten"-knop (h-7, text-xs, px-3, rounded-full).
// Supplier-/type-labels mogen over 2 regels wrappen om overlap met de status-kolom te voorkomen.
const PILL_BASE =
  "inline-flex items-center justify-center gap-1 min-h-7 px-3 py-1 text-xs font-semibold rounded-full ring-1 leading-tight text-center break-words max-w-full";
const PILL_TYPE = `${PILL_BASE} min-w-[7rem]`;
const PILL_DEFAULT = PILL_BASE;

function toneFor(variant: Variant, label: string): string {
  if (variant === "type") return TYPE_TONE[label as OrderDecoprofType] ?? NEUTRAL_TONE;
  return SUPPLIER_TONE;
}

function pillFor(variant: Variant): string {
  return variant === "type" ? PILL_TYPE : PILL_DEFAULT;
}

export interface BadgeItemMeta {
  toneClass?: string;
  icon?: React.ReactNode;
  tooltip?: string;
}

interface Props {
  items: ReadonlyArray<string>;
  variant: Variant;
  className?: string;
  /** R77: optionele per-label meta-overrides (key = label) */
  itemMeta?: Record<string, BadgeItemMeta>;
}

const Pill: React.FC<{
  label: string;
  variant: Variant;
  meta?: BadgeItemMeta;
}> = ({ label, variant, meta }) => {
  const cls = cn(pillFor(variant), meta?.toneClass ?? toneFor(variant, label));
  const inner = (
    <span className={cls}>
      {meta?.icon}
      {label}
    </span>
  );
  if (!meta?.tooltip) return inner;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild><span>{inner}</span></TooltipTrigger>
        <TooltipContent>{meta.tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const BadgeRow: React.FC<Props> = ({ items, variant, className, itemMeta }) => {
  const isMobile = useIsMobile();
  const max = isMobile ? 2 : 3;

  if (!items || items.length === 0) return null;

  const visible = items.slice(0, max);
  const hidden = items.slice(max);

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {visible.map((label) => (
        <Pill key={label} label={label} variant={variant} meta={itemMeta?.[label]} />
      ))}
      {hidden.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                PILL_DEFAULT,
                NEUTRAL_TONE,
                "cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary/40",
              )}
              aria-label={`${hidden.length} meer ${variant === "type" ? "types" : "leveranciers"} tonen`}
            >
              +{hidden.length}
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto max-w-[260px] p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap gap-1">
              {hidden.map((label) => (
                <Pill key={label} label={label} variant={variant} meta={itemMeta?.[label]} />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default BadgeRow;
